import { use, useEffect, useState } from "react";
import { usePlan } from "../_providers/PlanProvider";
import Widget from "../_components/Widget";
import CategoryController from "../controllers/CategoryController";
import Category from "../models/Category";
import { useAuth } from "../_providers/AuthProvider";
import { useLang } from "../_providers/LangProvider";
import PlanController from "../controllers/PlanController";
import { useModal } from "../_providers/ModalProvider";
import TransactionController from "../controllers/TransactionController";
import { ConfirmationModal } from "../_modals/ConfirmationModal";
import { PlanCategory } from "../models/Plan";

const ExpenseCategoriesWidget = () => {
    const { selectedPlan, reloadPlans, addPlanListener} = usePlan();
    const [currPlan, setCurrPlan] = useState(selectedPlan);
    const { user } = useAuth();
    const [expenseCategories, setExpenseCategories] = useState<Category[]>([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const { translate } = useLang();
    const { setModal } = useModal();
    const [draggedItem, setDraggedItem] = useState<Category | null>(null);
    const [hoverOver, setHoverOver] = useState<string | null>(null);
    const [hoverTop, setHoverTop] = useState<boolean>(false);

    const handleCheckboxChange = (category: string, type: "income" | "expense", value: boolean) => {
        if (!currPlan) {
            setError("No plan selected");
            return;
        }
        if (!user) {
            setError("Authentication Error");
            return;
        }
        if (value) {
            PlanController.addCategory(currPlan.uid, category, type)
                .then(() => reloadPlans())
                .catch((error) => setError("AddCategory: " + error.message));
        } else {
            const onConfirm = () => {
                PlanController.removeCategory(currPlan.uid, category, type)
                    .then(() => reloadPlans())
                    .catch((error) => setError("RemoveCategory: " + error.message));

                TransactionController.deleteCategoryTransactions(currPlan.uid, category)
                    .catch((error) => setError("DeleteTransactions: " + error.message));
            };
            setModal("Confirmation",
                <ConfirmationModal
                    message="Removing this category will also delete all current data related to this configuration, are you sure?"
                    onConfirm={onConfirm}
                />
            );
        }
    };

    const reloadData = () => {
        if (!currPlan) {
            setError("No plan selected");
            setLoading(false);
            return;
        }
        if (!user) {
            setError("Authentication Error");
            setLoading(false);
            return;
        }
        Promise.all([CategoryController.getCategories("expense")])
            .then(([expense_categories]) => {
                const sortedCategories = expense_categories.sort((a, b) => {
                    let catA = currPlan.expense_categories.find(cat => cat.uid === a.uid);
                    let catB = currPlan.expense_categories.find(cat => cat.uid === b.uid);
                    if (!catA) return 1;
                    if (!catB) return -1;
                    return catA.position - catB.position;
                });
                setExpenseCategories(sortedCategories);
                setLoading(false);
            })
            .catch((error) => {
                setError(error.message);
                setLoading(false);
            });
    };

    useEffect(() => {
        addPlanListener("ExpenseCategoriesWidget", (plan) => {
            setCurrPlan(plan);
        });
    }, []);

    useEffect(() => {
        reloadData();
    }, [currPlan]);

    const handleDragStart = (event: React.DragEvent<HTMLDivElement>, category: Category) => {
        setDraggedItem(category);
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", category.uid);
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>, category: string) => {
        event.preventDefault();
        setHoverOver(category);
        const div = event.currentTarget;
        const rect = div.getBoundingClientRect();
        const mouseY = event.clientY;
        const middleY = rect.top + rect.height / 2;
        if (mouseY < middleY) {
            setHoverTop(true);
        } else {
            setHoverTop(false);
        }
    };
    const handleDragLeave = () => {
    };

    const handleDrop = (category: Category) => {
        if(!currPlan) {
            setError("No plan selected");
            return; 
        }
        setHoverOver(null);
        if (!draggedItem || draggedItem.uid === category.uid) {
            setDraggedItem(null);
            return;
        };

        const reorderedCategories = [...expenseCategories];
        const draggedIndex = reorderedCategories.findIndex((item) => item.uid === draggedItem.uid);
        const targetIndex = reorderedCategories.findIndex((item) => item.uid === category.uid);

        if (draggedIndex !== -1 && targetIndex !== -1) {
            reorderedCategories.splice(draggedIndex, 1);
            reorderedCategories.splice( hoverTop && targetIndex - 1 >= 0 ? targetIndex - 1 : targetIndex, 0, draggedItem);
            const newPlanCategories: PlanCategory[] = reorderedCategories.map((item, index) => ({
                uid: item.uid,
                position: index,
            }));
            setExpenseCategories(reorderedCategories);
            PlanController.updateCategoryOrder(currPlan.uid, newPlanCategories.filter(x => currPlan.expense_categories.find(i => x.uid == i.uid)).map(x => x.uid), "expense").then(() => {
                reloadPlans();
            });
        }
        setDraggedItem(null);
    };

    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error}</p>;

    return (
        <Widget title="Expense Categories" flex={1} minWidth="450px">
            {expenseCategories.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10}}>
                    {expenseCategories.map((category) => (
                        <div 
                            style={{
                                transition: "ease-in-out 0.1s", 
                                overflow: "hidden",
                                opacity: draggedItem?.uid === category.uid ? "0.3" : "1",
                            }}
                            draggable
                            onDragStart={(e) => handleDragStart(e, category)}
                            onDragOver={(e) => handleDragOver(e, category.uid)}
                            onDragLeave={handleDragLeave}
                            onDragEnd={() => {setDraggedItem(null); setHoverOver(null);}}
                            onDrop={() => handleDrop(category)}
                        >
                        {hoverOver === category.uid && category.uid !== draggedItem?.uid && hoverTop ? <div style={{height: "50px", marginBottom: "10px", backgroundColor: "whitesmoke"}}/> : null}

                        <div
                            key={category.uid}
                            className="transaction dark"
                        >
                            <label className="checkbox">
                                {translate(category.uid)}
                                <input
                                    type="checkbox"
                                    onChange={(e) => handleCheckboxChange(category.uid, category.type, e.target.checked)}
                                    checked={currPlan?.expense_categories.find(x => x.uid === category.uid) !== undefined}
                                />
                                <span className="checkmark"></span>
                            </label>
                            <div className="inline_btn"
                            style={{ cursor: "grab"}}
                            onMouseDown={(e) => {
                                const transactionElement = e.currentTarget.parentElement; // Get parent div
                                if (transactionElement?.classList.contains("transaction")) {
                                    transactionElement.setAttribute("draggable", "true");
                                }
                            }}
                            onMouseUp={(e) => {
                                const transactionElement = e.currentTarget.parentElement;
                                if (transactionElement?.classList.contains("transaction")) {
                                    transactionElement.setAttribute("draggable", "false");
                                }
                            }}>
                                <i style={{opacity: 0.5}} className="fa-solid fa-grip-vertical" />
                            </div>
                        </div>
                        {hoverOver === category.uid && category.uid !== draggedItem?.uid && !hoverTop ? <div style={{height: "50px", marginBottom: "10px", backgroundColor: "whitesmoke"}}/> : null}
                        </div>
                    ))}
                </div>
            ) : (
                <p>No expense categories</p>
            )}
        </Widget>
    );
};

export default ExpenseCategoriesWidget;
